import { SharedProps } from '@/types'
import { Head, router } from '@inertiajs/react'
import { Button, Card, Checkbox, Code, Divider, Group, InputBase, NumberInput, Stack, Text, Textarea, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IMaskInput } from 'react-imask'

const SMS_MAX_LENGTH = 160

const WORKING_DAY_OPTIONS = [
  { value: '1', label: 'Lundi' },
  { value: '2', label: 'Mardi' },
  { value: '3', label: 'Mercredi' },
  { value: '4', label: 'Jeudi' },
  { value: '5', label: 'Vendredi' },
  { value: '6', label: 'Samedi' },
  { value: '0', label: 'Dimanche' },
]

const resolveSmsPreview = (template: string, organizationName: string) =>
  template
    .replace(/{nom_client}/g, 'Jean')
    .replace(/{numero_commande}/g, '123')
    .replace(/{nom_boutique}/g, organizationName)

export default function OrganizationsShowPage({ current_organization }: SharedProps) {
  if (!current_organization) return null

  const form = useForm({
    initialValues: {
      default_days_to_deliver: current_organization.default_days_to_deliver || 7,
      working_days: (current_organization.working_days || [1, 2, 3, 4, 5]).map(String),
      address: current_organization.address || '',
      phone_number: current_organization.phone_number || '',
      website: current_organization.website || '',
      sms_body: current_organization.sms_body || '',
      customer_receipt_footer: current_organization.customer_receipt_footer || '',
    },
    validate: {
      working_days: (value) =>
        current_organization.kind_atelier && value.length === 0 ? 'Sélectionnez au moins un jour' : null,
    },
  })

  const handleSubmit = (values: typeof form.values) => {
    router.patch(`/organizations/${current_organization.slug}/settings`, {
      organization: { ...values, working_days: values.working_days.map(Number) },
    })
  }

  return (
    <>
      <Head title={`Paramètres - ${current_organization.name}`} />

      <Stack maw={500} mx="auto" w="100%">
        <Title ta="center" order={2}>
          Paramètres
        </Title>

        <Card withBorder radius="lg" padding="xl" shadow="sm">
          <form
            onSubmit={form.onSubmit(handleSubmit)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
                event.preventDefault()
                form.onSubmit(handleSubmit)()
              }
            }}
          >
            <Stack gap="lg">
              <TextInput
                label="Adresse"
                size="md"
                {...form.getInputProps('address')}
              />

              <InputBase
                component={IMaskInput}
                mask="00 00 00 00 00"
                label="Numéro de téléphone"
                placeholder="01 00 00 00 00"
                size="md"
                {...form.getInputProps('phone_number')}
              />

              <TextInput label="Site web" size="md" {...form.getInputProps('website')} />

              {current_organization.kind_atelier && (
                <>
                  <Divider label="Livraison" labelPosition="center" />

                  <NumberInput
                    label="Délai de livraison par défaut"
                    description="En jours calendaires, ajustés au prochain jour de travail si nécessaire."
                    min={1}
                    size="md"
                    {...form.getInputProps('default_days_to_deliver')}
                  />

                  <Checkbox.Group
                    label="Jours de travail"
                    description="Jours pendant lesquels l'atelier est ouvert. Les autres jours sont ignorés lors du calcul de la date de livraison."
                    size="md"
                    {...form.getInputProps('working_days')}
                  >
                    <Group mt="xs" gap="md">
                      {WORKING_DAY_OPTIONS.map((option) => (
                        <Checkbox key={option.value} value={option.value} label={option.label} />
                      ))}
                    </Group>
                  </Checkbox.Group>
                </>
              )}

              <Divider label="SMS" labelPosition="center" />

              <Textarea
                label="Contenu du SMS"
                description="Variables disponibles : {nom_client}, {numero_commande}, {nom_boutique}"
                placeholder="Bonjour {nom_client}, votre commande {numero_commande} est prête !"
                autosize
                minRows={2}
                size="md"
                styles={
                  resolveSmsPreview(form.values.sms_body, current_organization.name).length > SMS_MAX_LENGTH
                    ? { input: { borderColor: 'var(--mantine-color-red-6)', color: 'var(--mantine-color-red-6)' } }
                    : undefined
                }
                {...form.getInputProps('sms_body')}
              />

              {form.values.sms_body && (
                <Stack gap={4} mt={-8}>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Aperçu du SMS
                    </Text>
                    <Text
                      size="sm"
                      c={resolveSmsPreview(form.values.sms_body, current_organization.name).length > SMS_MAX_LENGTH ? 'red' : 'dimmed'}
                    >
                      {resolveSmsPreview(form.values.sms_body, current_organization.name).length}/{SMS_MAX_LENGTH}
                    </Text>
                  </Group>
                  <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {resolveSmsPreview(form.values.sms_body, current_organization.name)}
                  </Code>
                </Stack>
              )}

              <Divider label="Impression" labelPosition="center" />

              <Textarea
                label="Message bas du ticket client"
                description="Imprimé au bas du bon de dépose remis au client."
                autosize
                minRows={3}
                size="md"
                {...form.getInputProps('customer_receipt_footer')}
              />

              <Group justify="flex-end">
                <Button type="submit" size="md" px="xl" radius="md">
                  Enregistrer les modifications
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </>
  )
}
